function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function parseCommandResult<T>(content: unknown): T {
  if (!isRecord(content) || !isRecord(content['data'])) {
    throw new Error('Command returned an invalid response');
  }
  const attributes = content['data']['attributes'];
  if (!isRecord(attributes) || !('result' in attributes)) {
    throw new Error('Command did not return a result');
  }
  return attributes['result'] as T;
}

/** Why a command request failed, as far as the thrown request error says. */
export interface CommandFailure {
  /** The HTTP status; 0 when no response arrived (20 when aborted), undefined for a non-request error. */
  status?: number;
  /** The server's own reason: the envelope's `message`, else the first JSON:API error's detail or title. */
  message?: string;
}

/** Reads a thrown `store.request` error; its `content` is a CommandResponse envelope or a kit JSON:API `errors` document. */
export function readCommandFailure(error: unknown): CommandFailure {
  const failure: CommandFailure = {};
  if (!isRecord(error)) return failure;
  if (typeof error['status'] === 'number') failure.status = error['status'];
  const message = commandFailureMessage(error['content']);
  if (message) failure.message = message;
  return failure;
}

function commandFailureMessage(content: unknown): string | undefined {
  if (!isRecord(content)) return undefined;

  const data = content['data'];
  const attributes = isRecord(data) ? data['attributes'] : undefined;
  const envelopeMessage = isRecord(attributes) ? attributes['message'] : undefined;
  if (typeof envelopeMessage === 'string' && envelopeMessage) return envelopeMessage;

  const errors = Array.isArray(content['errors']) ? content['errors'] : [];
  const first = errors.find(isRecord);
  const detail = first?.['detail'] || first?.['title'];
  return typeof detail === 'string' && detail ? detail : undefined;
}
